// dsv-engine.js — Calculation engine for DSV strategy-level yield simulation
// No UI dependencies. Importable by React, Node scripts, or agent tools.

const JS_RATIO_EPSILON = 1e-4; // floating-point tolerance for J:S ratio comparisons

// ── Default Strategy Configurations (from Dialectic-DSV-Yield-Estimations.xlsx) ──

const DEFAULT_STRATEGIES = [

  // Dawn Markets (with JT coverage)
  { name: "Fasanara mf-ONE", minCoverage: 0.15, allocation: 0, yieldShareToJunior: 0.17, underlyingYield: 0.09, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.05, recoveryDuration: 7, enabled: false },
  { name: "Apollo ACRED (Securitize)", minCoverage: 0.18, allocation: 0.05, yieldShareToJunior: 0.2, underlyingYield: 0.09, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.05, recoveryDuration: 7, enabled: true },
  { name: "Pareto FalconX", minCoverage: 0.12, allocation: 0, yieldShareToJunior: 0.15, underlyingYield: 0.0938, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.05, recoveryDuration: 7, enabled: false },
  { name: "Maple Institutional", minCoverage: 0.1, allocation: 0, yieldShareToJunior: 0.077, underlyingYield: 0.059, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.05, recoveryDuration: 7, enabled: false },
  { name: "Maple Syrup (public)", minCoverage: 0.1, allocation: 0.15, yieldShareToJunior: 0.09, underlyingYield: 0.045, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0, finalizedLossThreshold: 0, recoveryDuration: 0, enabled: true },
  { name: "Smokehouse USDC v1", minCoverage: 0.07, allocation: 0.15, yieldShareToJunior: 0.07, underlyingYield: 0.0431, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0, finalizedLossThreshold: 0, recoveryDuration: 0, enabled: true },
  { name: "Re Protocol reUSD", minCoverage: 0.1, allocation: 0, yieldShareToJunior: 0.09, underlyingYield: 0.06, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0, finalizedLossThreshold: 0, recoveryDuration: 0, enabled: false },
  { name: "sUSDai", minCoverage: 0.1, allocation: 0.2, yieldShareToJunior: 0.12, underlyingYield: 0.06, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0, finalizedLossThreshold: 0, recoveryDuration: 0, enabled: true },
  { name: "Neutrl sNUSD", minCoverage: 0.1, allocation: 0.18, yieldShareToJunior: 0.09, underlyingYield: 0.07, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.02, recoveryDuration: 7, enabled: true },
  { name: "Avant savUSD", minCoverage: 0.1, allocation: 0.17, yieldShareToJunior: 0.09, underlyingYield: 0.07, seniorFee: 0.1, juniorFee: 0, yieldShareFee: 0.45, selfLiquidationBonus: 0.01, finalizedLossThreshold: 0.02, recoveryDuration: 7, enabled: true },
  // Reserve Strategies (no JT, minCoverage = 0)
  { name: "Reserve Sky sUSDS", minCoverage: 0, allocation: 0.1, yieldShareToJunior: 0, underlyingYield: 0.04, seniorFee: 0, juniorFee: 0, yieldShareFee: 0, selfLiquidationBonus: 0, finalizedLossThreshold: 0, recoveryDuration: 0, enabled: true },
];

const DEFAULT_SIM_PARAMS = {
  reversionDays: 7,
  targetUtilization: 0.9,
  liquidationBonusMode: 'pessimistic',
  liquidationRedemptionRate: 0.3,
};

// ── Name Aliases (data source names → engine strategy names) ────────────────

const DATA_NAME_ALIASES = {
  "SUSDS":                  "Reserve Sky sUSDS",
  "Maple Syrup USDC":       "Maple Syrup (public)",
};

/**
 * Convert raw APY snapshot data (from yield-data-90day.json) into the
 * { dates, navs } timeseries format consumed by simulate().
 *
 * Handles two data source formats:
 *   - Morpho API: uses `net_apy` field (percentage, e.g. 4.23 = 4.23%)
 *   - DeFi Llama: uses `apy_total` field (percentage)
 *
 * For each strategy, computes a synthetic NAV series by compounding
 * the daily rate implied by each day's APY snapshot:
 *   dailyRate = (1 + apy/100)^(1/365) - 1
 *   nav[t+1] = nav[t] * (1 + dailyRate)
 *
 * Strategy names are aliased via DATA_NAME_ALIASES to match engine defaults.
 * Unmatched names are passed through as-is.
 *
 * @param {object} rawData - The raw JSON object keyed by data source name
 * @returns {object} Map of engine strategy name → { dates: string[], navs: number[] }
 */
function convertApyData(rawData) {
  const result = {};

  for (const [dataName, entry] of Object.entries(rawData)) {
    if (dataName === 'metadata') continue;
    const engineName = DATA_NAME_ALIASES[dataName] || dataName;
    const source = entry.source || "";
    const rawEntries = entry.data || {};

    // Collect and sort dates chronologically
    const sortedDates = Object.keys(rawEntries).sort();
    if (sortedDates.length === 0) continue;

    const dates = [];
    const navs = [];
    let nav = 1.0;

    for (let i = 0; i < sortedDates.length; i++) {
      const date = sortedDates[i];
      const snapshot = rawEntries[date];

      // Extract APY based on source
      let apy;
      if (source.includes("Morpho")) {
        apy = snapshot.net_apy;
      } else {
        apy = snapshot.apy_total;
      }

      // Skip days with null/undefined/NaN APY
      if (apy == null || isNaN(apy)) continue;

      if (i === 0) {
        // First day: just record starting NAV
        dates.push(date);
        navs.push(nav);
      } else {
        // Convert APY (percentage) to daily rate, compound NAV
        const dailyRate = Math.pow(1 + apy / 100, 1 / 365) - 1;
        nav *= (1 + dailyRate);
        dates.push(date);
        navs.push(nav);
      }
    }

    if (dates.length >= 2) {
      result[engineName] = { dates, navs };
    }
  }

  return result;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute the Junior-to-Senior capital ratio at the kink (target utilization).
 * For reserve strategies (minCoverage = 0), returns 0 (no junior tranche).
 *
 * Formula: J:S = minCoverage / (targetUtil - minCoverage)
 *
 * @param {number} minCoverage - Minimum JT coverage ratio (e.g. 0.10 = 10%)
 * @param {number} targetUtil  - Target utilization (e.g. 0.9 = 90%)
 * @returns {number} The J:S ratio at the kink
 */
function computeJsRatio(minCoverage, targetUtil) {
  if (minCoverage <= 0) return 0;
  const denom = targetUtil - minCoverage;
  if (denom <= 0) return Infinity;
  return minCoverage / denom;
}

/**
 * Compute the Liquidation Loan-to-Value (LLTV) threshold.
 * When LTV >= LLTV, losses are booked immediately.
 *
 * Formula: LLTV = 1 / ((1 - FLT) * (1 + J:S_ratio))
 *
 * @param {number} flt     - Finalized Loss Threshold (e.g. 0.05 = 5%)
 * @param {number} jsRatio - Junior-to-Senior capital ratio
 * @returns {number} LLTV threshold (1.0 means no JT protection)
 */
function computeLltv(flt, jsRatio) {
  if (flt <= 0 || jsRatio <= 0) return 1;
  return 1 / ((1 - flt) * (1 + jsRatio));
}

/**
 * Generate synthetic daily NAV series from a constant annual yield.
 * Produces (days + 1) data points: day 0 = startDate with NAV = 1.0,
 * then daily compounding at the given annual rate.
 *
 * @param {number} annualYield - Annual yield as decimal (e.g. 0.09 = 9%)
 * @param {number} days        - Number of days to simulate (default 365)
 * @param {Date}   [startDate] - Start date (default: 2025-01-01)
 * @returns {{ dates: string[], navs: number[] }}
 */
function generateSyntheticNav(annualYield, days = 365, startDate = new Date("2025-01-01")) {
  const dailyReturn = Math.pow(1 + annualYield, 1 / 365) - 1;
  const dates = [];
  const navs = [];
  let nav = 1.0;

  const startMs = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
  for (let d = 0; d <= days; d++) {
    const ms = startMs + d * 86400000;
    const dt = new Date(ms);
    dates.push(dt.toISOString().slice(0, 10));
    navs.push(nav);
    nav *= (1 + dailyReturn);
  }

  return { dates, navs };
}

// ── Core Simulation ─────────────────────────────────────────────────────────

/**
 * Main DSV simulation engine.
 *
 * Simulates the daily performance of a Dialectic Senior Vault (DSV) composed
 * of multiple underlying strategies, each with a senior tranche (ST) and
 * optional junior tranche (JT). The JT absorbs losses first; the ST earns
 * yield minus fees. Reserve strategies have no JT (minCoverage = 0).
 *
 * @param {object}   opts
 * @param {object[]} [opts.strategies]       - Array of strategy config objects (defaults to DEFAULT_STRATEGIES)
 * @param {object}   [opts.navData]          - Map of strategy name to { dates: string[], navs: number[] }
 * @param {object}   [opts.simParams]        - { reversionDays, targetUtilization }
 * @param {number}   [opts.steadyStateDays]  - Days to simulate with synthetic NAV (default 365)
 * @param {Date}     [opts.startDate]        - Start date for synthetic NAV generation
 *
 * @returns {object} {
 *   dates: string[],
 *   strategies: { [name]: { seniorCumReturn[], underlyingCumReturn[], jtEffectiveNav[], summary } },
 *   aggregate: { dsvCumReturn[], summary }
 * }
 */
function simulate({
  strategies = DEFAULT_STRATEGIES,
  navData = null,
  simParams = {},
  steadyStateDays = 365,
  startDate,
} = {}) {
  const params = { ...DEFAULT_SIM_PARAMS, ...simParams };
  const { reversionDays, targetUtilization } = params;

  // ── 1. Filter enabled strategies, normalize allocations ──
  const enabled = strategies.filter(s => s.enabled && s.allocation > 0);
  if (enabled.length === 0) {
    return {
      dates: [],
      strategies: {},
      aggregate: { dsvCumReturn: [], summary: { annualizedReturn: 0, totalReturn: 0, days: 0 } },
    };
  }

  const normalized = enabled.map(s => ({
    ...s,
    weight: s.allocation,
  }));

  // ── 2. Resolve NAV data (provided or synthetic) ──
  // When real navData is present, derive the date range from it so synthetic
  // series for strategies without data cover the same period.
  let syntheticStart = startDate ? new Date(startDate) : undefined;
  let syntheticDays = steadyStateDays;

  if (navData) {
    const allDates = Object.values(navData).flatMap(nd => nd.dates || []);
    if (allDates.length > 0) {
      const sorted = [...new Set(allDates)].sort();
      syntheticStart = new Date(sorted[0]);
      const endMs = Date.parse(sorted[sorted.length - 1]);
      const startMs = Date.parse(sorted[0]);
      syntheticDays = Math.round((endMs - startMs) / 86400000);
    }
  }

  const resolvedNav = {};
  for (const s of normalized) {
    if (navData && navData[s.name]) {
      resolvedNav[s.name] = navData[s.name];
    } else {
      resolvedNav[s.name] = generateSyntheticNav(
        s.underlyingYield,
        syntheticDays,
        syntheticStart,
      );
    }
  }

  // ── 2b. Forward-fill NAV gaps (weekends, holidays) ──
  // For each strategy, fill missing calendar days with the previous day's NAV
  // so that gaps don't shrink the common date intersection.
  for (const s of normalized) {
    const nd = resolvedNav[s.name];
    if (nd.dates.length < 2) continue;
    const startMs = Date.parse(nd.dates[0]);
    const endMs = Date.parse(nd.dates[nd.dates.length - 1]);
    const totalDays = Math.round((endMs - startMs) / 86400000);
    if (totalDays === nd.dates.length - 1) continue; // already contiguous
    const navByDate = {};
    for (let i = 0; i < nd.dates.length; i++) {
      navByDate[nd.dates[i]] = nd.navs[i];
    }
    const filledDates = [];
    const filledNavs = [];
    let prevNav = nd.navs[0];
    for (let d = 0; d <= totalDays; d++) {
      const ms = startMs + d * 86400000;
      const dt = new Date(ms).toISOString().slice(0, 10);
      const nav = navByDate[dt] != null ? navByDate[dt] : prevNav;
      filledDates.push(dt);
      filledNavs.push(nav);
      prevNav = nav;
    }
    resolvedNav[s.name] = { dates: filledDates, navs: filledNavs };
  }

  // ── 3. Find common date range ──
  // Build a set of dates present across ALL strategies
  const dateSets = normalized.map(s => new Set(resolvedNav[s.name].dates));
  let commonDates = [...dateSets[0]].filter(d => dateSets.every(ds => ds.has(d)));
  commonDates.sort();

  if (commonDates.length < 2) {
    return {
      dates: commonDates,
      strategies: {},
      aggregate: { dsvCumReturn: commonDates.map(() => 0), summary: { annualizedReturn: 0, totalReturn: 0, days: 0 } },
    };
  }

  // Build per-strategy NAV lookup (date -> nav value) for quick access
  const navLookup = {};
  for (const s of normalized) {
    const lookup = {};
    const nd = resolvedNav[s.name];
    for (let i = 0; i < nd.dates.length; i++) {
      lookup[nd.dates[i]] = nd.navs[i];
    }
    navLookup[s.name] = lookup;
  }

  // ── 4. Initialize per-strategy state ──
  const strategyState = {};
  for (const s of normalized) {
    const jsRatio = computeJsRatio(s.minCoverage, targetUtilization);
    const isReserve = s.minCoverage <= 0;

    const lltv = computeLltv(s.finalizedLossThreshold || 0, jsRatio);

    strategyState[s.name] = {
      stCapital: 1.0,           // Senior tranche capital (normalized to 1)
      jtCapital: jsRatio,       // Junior tranche capital (J:S ratio)
      jsRatioInitial: jsRatio,  // Initial J:S ratio at the kink
      jsRatioCurrent: jsRatio,  // Current J:S ratio (tracks depletion/reversion)
      isReserve,
      prevNav: navLookup[s.name][commonDates[0]],
      seniorCumReturn: [0],     // Cumulative return series for senior tranche
      underlyingCumReturn: [0], // Cumulative return of the underlying strategy
      jtEffectiveNav: [jsRatio > 0 ? 1.0 : 0], // Effective JT NAV (starts at 1.0 if JT exists)
      // State machine fields
      marketState: 'PERPETUAL',
      fixedTermEndDay: 0,
      lltv,
      marketStateHistory: ['PERPETUAL'],
      lossBookingEvents: [],
    };
  }

  // ── 5. Daily loop ──
  const numDays = commonDates.length;

  for (let dayIdx = 1; dayIdx < numDays; dayIdx++) {
    const date = commonDates[dayIdx];

    for (const s of normalized) {
      const state = strategyState[s.name];
      const currentNav = navLookup[s.name][date];
      const dailyReturn = (currentNav - state.prevNav) / state.prevNav;
      state.prevNav = currentNav;

      // Track underlying cumulative return
      const prevUnderlyingCum = state.underlyingCumReturn[state.underlyingCumReturn.length - 1];
      const newUnderlyingCum = (1 + prevUnderlyingCum) * (1 + dailyReturn) - 1;
      state.underlyingCumReturn.push(newUnderlyingCum);

      if (state.isReserve) {
        // Reserve strategies: no JT, no fees, senior gets full return
        const prevSeniorCum = state.seniorCumReturn[state.seniorCumReturn.length - 1];
        const newSeniorCum = (1 + prevSeniorCum) * (1 + dailyReturn) - 1;
        state.seniorCumReturn.push(newSeniorCum);
        state.jtEffectiveNav.push(0);
        state.marketStateHistory.push('PERPETUAL');
        continue;
      }

      // ── Helper: book losses (finalize JT depletion, return to PERPETUAL) ──
      const bookLosses = () => {
        // Apply self-liquidation bonus: drains JT capital (compensates for
        // secondary market spread, not a return for ST)
        if ((s.selfLiquidationBonus || 0) > 0 && state.jtCapital > 0) {
          const redeemingCapital = params.liquidationBonusMode === 'optimistic'
            ? state.stCapital * (params.liquidationRedemptionRate ?? 0.3)
            : state.stCapital;
          const desiredBonus = redeemingCapital * s.selfLiquidationBonus;
          const actualBonus = Math.min(desiredBonus, state.jtCapital);
          state.jtCapital -= actualBonus;
          // Update J:S ratio after drain
          state.jsRatioCurrent = state.stCapital > 0
            ? state.jtCapital / state.stCapital
            : 0;
        }
        state.lossBookingEvents.push({
          dayIndex: dayIdx,
          date,
          jtCapitalAtBooking: state.jtCapital,
          stCapitalAtBooking: state.stCapital,
        });
        state.marketState = 'PERPETUAL';
      };

      // ── TIMER CHECK: If FIXED_TERM and timer expired ──
      if (state.marketState === 'FIXED_TERM' && dayIdx >= state.fixedTermEndDay) {
        bookLosses();
      }

      // ── Drawdown scenario: dailyReturn < 0 ──
      if (dailyReturn < 0) {
        const loss = -dailyReturn; // positive magnitude of loss
        const totalCapital = state.stCapital + state.jtCapital;
        const dollarLoss = loss * totalCapital;

        if (state.jtCapital >= dollarLoss) {
          // JT absorbs all losses
          state.jtCapital -= dollarLoss;
        } else {
          // JT exhausted, senior takes the remainder
          const seniorLoss = dollarLoss - state.jtCapital;
          state.jtCapital = 0;
          state.stCapital = Math.max(0, state.stCapital - seniorLoss);
        }

        // Update J:S ratio
        state.jsRatioCurrent = state.stCapital > 0
          ? state.jtCapital / state.stCapital
          : 0;

        // Check LTV for LLTV breach (FIXED_TERM only)
        if (state.marketState === 'FIXED_TERM' && state.stCapital > 0) {
          const ltv = state.stCapital / (state.stCapital + state.jtCapital);
          if (ltv >= state.lltv) {
            bookLosses();
          }
        }
        // Enter FIXED_TERM from PERPETUAL if JT depleted but not fully wiped
        // Skip if FLT=0 or recoveryDuration=0 — losses are immediately absorbed, no recovery period
        else if (state.marketState === 'PERPETUAL'
          && state.jtCapital < state.jsRatioInitial * state.stCapital * (1 - JS_RATIO_EPSILON)
          && state.jtCapital > 0
          && (s.finalizedLossThreshold || 0) > 0
          && (s.recoveryDuration || 0) > 0) {
          state.marketState = 'FIXED_TERM';
          state.fixedTermEndDay = dayIdx + (s.recoveryDuration || 7);
        }

        // Push daily outputs
        state.seniorCumReturn.push(state.stCapital - 1.0);
        state.jtEffectiveNav.push(
          state.jsRatioInitial > 0 ? state.jtCapital / state.jsRatioInitial : 0
        );
        state.marketStateHistory.push(state.marketState);
        continue;
      }

      // ── Gain scenario: dailyReturn >= 0 ──

      if (state.marketState === 'FIXED_TERM') {
        // 100% yield to JT (additive, no fees); senior capital frozen
        const totalYield = dailyReturn * (state.stCapital + state.jtCapital);
        state.jtCapital += totalYield;

        // Update J:S ratio
        state.jsRatioCurrent = state.stCapital > 0
          ? state.jtCapital / state.stCapital
          : 0;

        // Check if JT has fully recovered
        if (state.jtCapital >= state.jsRatioInitial * state.stCapital * (1 - JS_RATIO_EPSILON)) {
          state.marketState = 'PERPETUAL';
          state.jsRatioCurrent = state.jsRatioInitial;
          state.jtCapital = state.jsRatioInitial * state.stCapital;
        }
      } else {
        // PERPETUAL: yield split with inverse scaling when JT is depleted.
        // When recoveryDuration=0, losses are permanently booked — skip recovery routing
        // so future yield splits via normal YDM rather than diverting to JT.
        let yieldShareJT = s.yieldShareToJunior;

        if (state.jsRatioInitial > 0 && state.jsRatioCurrent < state.jsRatioInitial * (1 - JS_RATIO_EPSILON)
          && (s.recoveryDuration || 0) > 0) {
          if (state.jsRatioCurrent > 0) {
            yieldShareJT = Math.min(1.0, s.yieldShareToJunior * (state.jsRatioInitial / state.jsRatioCurrent));
          } else {
            yieldShareJT = 1.0;
          }
        }

        const grossSeniorReturn = dailyReturn * (1 - yieldShareJT);
        const netSeniorReturn = grossSeniorReturn * (1 - s.seniorFee);
        state.stCapital *= (1 + netSeniorReturn);

        const yieldShareComponent = dailyReturn * yieldShareJT / (state.jsRatioInitial || 1);
        const yieldShareComponentNet = yieldShareComponent * (1 - (s.yieldShareFee || 0));
        const grossJuniorReturn = dailyReturn + yieldShareComponentNet;
        const netJuniorReturn = grossJuniorReturn * (1 - s.juniorFee);
        state.jtCapital *= (1 + netJuniorReturn);

        // Update J:S ratio
        state.jsRatioCurrent = state.stCapital > 0
          ? state.jtCapital / state.stCapital
          : 0;

        // ── J:S ratio reversion (PERPETUAL only, skip when recoveryDuration=0) ──
        if (state.jsRatioCurrent < state.jsRatioInitial * (1 - JS_RATIO_EPSILON) && reversionDays > 0
          && (s.recoveryDuration || 0) > 0) {
          const step = state.jsRatioInitial / reversionDays;
          state.jsRatioCurrent = Math.min(
            state.jsRatioCurrent + step,
            state.jsRatioInitial,
          );
          state.jtCapital = state.jsRatioCurrent * state.stCapital;

          if (state.jsRatioCurrent >= state.jsRatioInitial * (1 - JS_RATIO_EPSILON)) {
            state.jsRatioCurrent = state.jsRatioInitial;
            state.jtCapital = state.jsRatioInitial * state.stCapital;
          }
        }
      }

      // Senior cumulative return: stCapital tracks total growth from initial 1.0
      state.seniorCumReturn.push(state.stCapital - 1.0);
      state.jtEffectiveNav.push(
        state.jsRatioInitial > 0 ? state.jtCapital / state.jsRatioInitial : 0
      );
      state.marketStateHistory.push(state.marketState);
    }
  }

  // ── 6. Compute aggregate DSV return as allocation-weighted sum ──
  const dsvCumReturn = new Array(numDays).fill(0);
  for (let i = 0; i < numDays; i++) {
    for (const s of normalized) {
      const state = strategyState[s.name];
      dsvCumReturn[i] += s.weight * state.seniorCumReturn[i];
    }
  }

  // ── 7. Build return object ──
  const strategyResults = {};
  for (const s of normalized) {
    const state = strategyState[s.name];
    const totalDays = numDays - 1;
    const totalReturn = state.seniorCumReturn[state.seniorCumReturn.length - 1];
    const annualizedReturn = totalDays > 0
      ? Math.pow(1 + totalReturn, 365 / totalDays) - 1
      : 0;

    const fixedTermDays = state.marketStateHistory.filter(ms => ms === 'FIXED_TERM').length;

    strategyResults[s.name] = {
      seniorCumReturn: state.seniorCumReturn,
      underlyingCumReturn: state.underlyingCumReturn,
      jtEffectiveNav: state.jtEffectiveNav,
      marketStateHistory: state.marketStateHistory,
      lossBookingEvents: state.lossBookingEvents,
      lltv: state.lltv,
      summary: {
        totalReturn,
        annualizedReturn,
        days: totalDays,
        jsRatioInitial: state.jsRatioInitial,
        isReserve: state.isReserve,
        weight: s.weight,
        fixedTermDays,
        lossBookings: state.lossBookingEvents.length,
        finalizedLossThreshold: s.finalizedLossThreshold || 0,
        recoveryDuration: s.recoveryDuration || 0,
      },
    };
  }

  const aggTotalReturn = dsvCumReturn[dsvCumReturn.length - 1];
  const aggDays = numDays - 1;
  const aggAnnualized = aggDays > 0
    ? Math.pow(1 + aggTotalReturn, 365 / aggDays) - 1
    : 0;

  return {
    dates: commonDates,
    strategies: strategyResults,
    aggregate: {
      dsvCumReturn,
      summary: {
        totalReturn: aggTotalReturn,
        annualizedReturn: aggAnnualized,
        days: aggDays,
      },
    },
  };
}

export { DEFAULT_STRATEGIES, DEFAULT_SIM_PARAMS, DATA_NAME_ALIASES, simulate, computeJsRatio, computeLltv, generateSyntheticNav, convertApyData };
